import { z } from 'zod';
import { assetKindSchema } from './enums.js';
import { EXIF_TRACKED_FIELDS } from './exif.js';
import { SAVED_FILTER_EXPR_MAX } from './saved-filter.js';
/**
 * Contrats de la grille galerie (T2b, XPHOTO-11/-18/-20). Source unique front + back.
 *
 * - `assetQuerySchema`  : query params de `GET /api/libraries/:id/assets`
 *   (tri / filtres / recherche / pagination fenêtrée).
 * - `galleryAssetSchema`: DTO LÉGER par vignette (sous-ensemble d'`assetSchema`),
 *   suffisant pour peindre une grille virtualisée sans charger l'EXIF ni les hashes.
 * - `assetPageSchema`   : page renvoyée (`items` + `total` + `nextCursor?`).
 * - `folderFacetSchema` / `tagFacetSchema` : facettes des onglets Dossiers / Tags.
 * - `assetCountsSchema` : compteurs de la barre d'outils (présents + par état).
 */
/**
 * Clés de tri autorisées (allow-list ; jamais d'entrée brute dans le SQL).
 *
 * `random` = ordre ALÉATOIRE mais STABLE pour une graine (`seed`) donnée : l'ordre
 * est `md5(seed || id)` côté repo → une même graine redonne toujours le même ordre
 * (pagination + visionneuse cohérentes), une graine différente re-mélange. Sert de
 * défaut de l'Accueil (re-mélangé à chaque session, cf. `gallery/random-seed.ts`).
 */
export const assetSortSchema = z.enum([
    'takenAt',
    'filename',
    'sizeBytes',
    'createdAt',
    'viewCount',
    'rating',
    'random',
]);
/**
 * Format d'une graine d'aléatoire (`seed`) : token borné alphanumérique + `-`/`_`
 * (URL-safe), 1..64 caractères. Validé côté route (jamais d'entrée brute dans le SQL —
 * la graine transite de toute façon comme un PARAMÈTRE lié `$n`, jamais concaténée).
 */
export const ASSET_SEED_PATTERN = /^[A-Za-z0-9_-]{1,64}$/;
export const assetSeedSchema = z.string().regex(ASSET_SEED_PATTERN);
/**
 * Découpe une querystring CSV (`"a,b,c"`) en tableau dédupliqué de segments non
 * vides. Sert de `z.preprocess` aux filtres multi-valeurs (`kinds`, `tagIds`) :
 * la querystring arrive en STRING ; le `.meta({ type: 'string', … })` de chaque
 * filtre présente le champ comme une STRING CSV pour AJV (jamais `type: array`,
 * qui ferait rejeter le CSV) et efface les vestiges `items`/`minItems` du tableau.
 * `.transform` est à proscrire (piège `z.toJSONSchema` au boot).
 */
const splitCsv = (value) => {
    if (typeof value !== 'string')
        return value;
    const parts = value
        .split(',')
        .map((s) => s.trim())
        .filter((s) => s.length > 0);
    return [...new Set(parts)];
};
/** Sens de tri. */
export const sortDirSchema = z.enum(['asc', 'desc']);
/**
 * État de publication d'un asset (mots-clés du fichier vs tags validés en base) :
 * `inSync` = déjà à jour, `outOfSync` = divergent (à publier). Filtre du lien
 * profond depuis l'Outil EXIF (XPHOTO-24 → -06).
 */
export const publishStateFilterSchema = z.enum(['inSync', 'outOfSync']);
/**
 * Types de média sélectionnables en MULTIPLE dans la « recherche avancée » (facette
 * « Type » à cases). Distinct d'`assetKindSchema` (image|video) : il expose la 3e
 * catégorie « animée » de l'UI — `image` = images STATIQUES seulement, `animatedImage`
 * = images animées (GIF/WebP/APNG), `video` = vidéos.
 */
export const mediaKindFilterSchema = z.enum(['image', 'animatedImage', 'video']);
/**
 * Sélection MULTIPLE de types (`?kinds=image,video`). MÊME patron que
 * `statesFilterSchema` (CSV via `z.preprocess`/`.meta` — jamais de `.transform`, piège
 * boot `z.toJSONSchema`). Union (OU) des types cochés côté repo. Absent ⇒ aucun filtre
 * Type. Tokens dédupliqués.
 */
export const kindsFilterSchema = z
    .preprocess(splitCsv, z.array(mediaKindFilterSchema).nonempty())
    .meta({ type: 'string', minItems: undefined, items: undefined })
    .optional();
/**
 * Sélection MULTIPLE de tags par id (`?tags=uuid1,uuid2`), combinée à `tagMode`
 * (`all`/`any`). MÊME patron CSV que `statesFilterSchema` (dédup, jamais de `.transform`).
 * Chaque élément est un uuid. Absent ⇒ aucun filtre multi-tags.
 */
export const tagIdsFilterSchema = z
    .preprocess(splitCsv, z.array(z.uuid()).nonempty())
    .meta({ type: 'string', minItems: undefined, items: undefined })
    .optional();
/** Mode de combinaison de `tagIds` : `all` (ET — porte tous) / `any` (OU — au moins un). */
export const tagModeSchema = z.enum(['all', 'any']);
/** Borne haute de la fenêtre (protège la DB). */
export const ASSET_QUERY_MAX_LIMIT = 500;
export const ASSET_QUERY_DEFAULT_LIMIT = 100;
/**
 * Query params de la liste d'assets. Tous les filtres sont optionnels ; le WHERE
 * est construit dynamiquement côté repo, 100 % paramétré. Les valeurs de tri
 * viennent d'une allow-list (map `sort`→colonne), jamais concaténées.
 *
 * Pagination : OFFSET/LIMIT (voir compte-rendu). `total` dimensionne le scroller
 * virtualisé ; `offset`/`limit` fournissent la fenêtre. Tri stable (départage `id`).
 */
export const assetQuerySchema = z.object({
    // --- pagination fenêtrée ---
    offset: z.coerce.number().int().nonnegative().default(0),
    limit: z.coerce
        .number()
        .int()
        .positive()
        .max(ASSET_QUERY_MAX_LIMIT)
        .default(ASSET_QUERY_DEFAULT_LIMIT),
    // --- tri ---
    sort: assetSortSchema.default('takenAt'),
    dir: sortDirSchema.default('desc'),
    /**
     * Graine de l'ordre ALÉATOIRE (`sort=random`). Token borné (`ASSET_SEED_PATTERN`) →
     * l'ordre `md5(seed || id)` est STABLE pour une même graine (pagination + rang
     * `position` cohérents), et re-mélangé par une graine différente. Ignoré hors
     * `sort=random`. Absent avec `sort=random` ⇒ ordre déterministe par `id` (garde-fou ;
     * le client envoie toujours une graine).
     */
    seed: assetSeedSchema.optional(),
    // --- filtres ---
    /**
     * Dossier ciblé (dérivé de `rel_path`). Sans `folderDirect`, c'est un préfixe
     * RÉCURSIF (dossier + tout son sous-arbre — onglet Dossiers historique, liens
     * profonds EXIF, Galerie). La chaîne VIDE `''` est autorisée : combinée à
     * `folderDirect`, elle cible la RACINE (fichiers sans `/`). Absent (`undefined`) ⇒
     * aucun filtre de dossier (distinct de `''`).
     */
    folder: z.string().optional(),
    /**
     * Navigation HIÉRARCHIQUE (explorateur de fichiers). Combiné à `folder`, ne renvoie
     * que les photos DIRECTEMENT dans ce dossier — le répertoire immédiat de l'asset est
     * EXACTEMENT `folder` — jamais les fichiers des sous-dossiers. `folder=''` +
     * `folderDirect=true` = fichiers à la racine. Absent/`false` ⇒ prédicat `folder`
     * RÉCURSIF classique. `z.coerce` : la querystring arrive en string.
     */
    folderDirect: z.coerce.boolean().optional(),
    /** image | video. */
    kind: assetKindSchema.optional(),
    /** Plage de dates EXIF (bornes ISO 8601, incluses). */
    takenFrom: z.string().datetime({ offset: true }).optional(),
    takenTo: z.string().datetime({ offset: true }).optional(),
    /** Tag par id (uuid) OU par label (insensible casse/accents), via `asset_tags` validés. */
    tag: z.string().min(1).optional(),
    /** N'inclure que les assets porteurs d'au moins une note. */
    hasNotes: z.coerce.boolean().optional(),
    /**
     * N'inclure que les assets porteurs d'au moins un COMMENTAIRE TYPÉ de ce type
     * (insensible à la casse en base). Cherchabilité par type des commentaires.
     */
    commentType: z.string().min(1).max(64).optional(),
    /**
     * N'inclure que les vidéos porteuses d'une piste audio (`has_audio = true`) —
     * facette « Avec son ». `true` filtre ; absent ⇒ pas de filtre. Les images et
     * vidéos muettes (has_audio null/false) sont exclues quand le filtre est actif.
     */
    hasAudio: z.coerce.boolean().optional(),
    /**
     * Filtre « image animée » (GIF/WebP/APNG). `true` = uniquement les images animées,
     * `false` = uniquement les images statiques (hors animées) ; absent ⇒ pas de filtre.
     * Prédicat côté repo : `(is_animated = true OR mime = 'image/gif')` — les GIF sont
     * traités comme animés SANS re-scan (le WebP/APNG animé après le prochain scan).
     * Utilisé avec `kind: 'image'` par la section « Type » de la barre d'outils.
     */
    animated: z.coerce.boolean().optional(),
    // --- recherche avancée (facettes combinées en ET avec les filtres ci-dessus) ---
    /**
     * INTERVALLE de note (0..5 ; 0 = non noté). `ratingMin` seul = « au moins N★ »,
     * les deux = intervalle inclus. `z.coerce` : la querystring arrive en string.
     */
    ratingMin: z.coerce.number().int().min(0).max(5).optional(),
    ratingMax: z.coerce.number().int().min(0).max(5).optional(),
    /**
     * Sélection MULTIPLE de types (`?kinds=image,animatedImage,video`) — union (OU) des
     * types cochés. Coexiste avec `kind`+`animated` (single) sans les remplacer.
     */
    kinds: kindsFilterSchema,
    /**
     * Sélection MULTIPLE de tags par id (`?tags=uuid1,uuid2`), liaisons `validated`.
     * Combinée à `tagMode` : `any` (défaut) = porte AU MOINS un ; `all` = porte TOUS.
     * Coexiste avec `tag` (single, id/label — lien profond & facette).
     */
    tagIds: tagIdsFilterSchema,
    /** Mode de `tagIds` (`all`/`any`, défaut `any`). Ignoré sans `tagIds`. */
    tagMode: tagModeSchema.optional(),
    /**
     * N'inclure que les assets dont `exif_snapshot` porte ce champ EXIF (allow-list
     * `EXIF_TRACKED_FIELDS` ; jamais d'entrée brute dans le SQL). Lien profond des
     * cartes d'analyse de l'Outil EXIF (XPHOTO-24) vers la galerie.
     */
    hasExifField: z.enum(EXIF_TRACKED_FIELDS).optional(),
    /**
     * N'inclure que les assets dans cet état de publication (`inSync`/`outOfSync`) —
     * lien profond du panneau publication de l'Outil EXIF (XPHOTO-24 → -06).
     */
    publishState: publishStateFilterSchema.optional(),
    /**
     * Filtre « statué / non statué » du nouveau système « Taguer » : `false` =
     * uniquement les photos NON encore taguées (« à traiter » du tagging), `true` =
     * uniquement les taguées ; absent ⇒ pas de filtre. Un humain a-t-il commit les
     * tags de la photo (drapeau `assets.tagged`, cf. migration 0025) ?
     */
    tagged: z.coerce.boolean().optional(),
    /**
     * Filtre « À valider (IA) » : uniquement les photos NON encore statuées (`tagged=false`)
     * porteuses d'AU MOINS UNE liaison de tag posée par l'IA (`asset_tags.source='ai'`, quel
     * que soit son statut — proposée OU déjà acceptée en `validated`). Une photo dont on a
     * « accepté » les suggestions IA (proposed→validated) reste donc À VALIDER tant qu'un
     * humain ne l'a pas finalisée (`tagged=true`). Sert au « pont » de la cloche et à l'option
     * « À valider (IA) » du menu Filtrer / au raccourci d'en-tête. Plus étroit que
     * « Non taguées » (qui inclut aussi les photos jamais touchées par l'IA). Absent ⇒ pas de
     * filtre. `true` seulement (booléen simple, sans `.transform`).
     */
    pendingAiTags: z.coerce.boolean().optional(),
    /** Inclure les assets absents du disque (défaut : exclus). */
    includeMissing: z.coerce.boolean().default(false),
    // --- recherche ---
    /** Sur `filename` (insensible casse/accents). N'inclut PAS le corps des notes. */
    q: z.string().min(1).optional(),
    /**
     * Recherche TEXTE dans les ANNOTATIONS (T8/XPHOTO-13, cœur de l'API externe) :
     * n'inclut que les assets dont AU MOINS UNE annotation contient ce texte —
     * couvre à la fois les COMMENTAIRES TYPÉS (`asset_comments.body`, l'annotation
     * active) ET les NOTES historiques (`notes.body`). Insensible à la casse ET aux
     * accents (même normalisation que `q`, servie par les index trigram 0016). Axe
     * INDÉPENDANT de `q` (filename) : ils se composent en ET si les deux sont fournis.
     */
    annotation: z.string().min(1).max(200).optional(),
    /**
     * Expression de filtre booléenne (mini-langage `filter-expr.ts`) — filtre AVANCÉ. Texte
     * borné en longueur ; parsé/validé côté route (400 si invalide). ANDé avec tous les autres
     * axes (dossier, type, note, tag, recherche…) ET par-dessus le cloisonnement obligatoire
     * (library_id, corbeille, absents, sous-arbres) — jamais un moyen de le contourner. Absent
     * ⇒ pas de filtre avancé. Simple `string` (AUCUN `.transform` : garde `z.toJSONSchema`).
     */
    expr: z.string().max(SAVED_FILTER_EXPR_MAX).optional(),
});
/**
 * DTO léger d'un asset pour la grille. Sous-ensemble d'`assetSchema` : de quoi
 * peindre une tuile (ratio, badge d'état, absence) et bâtir l'URL de vignette
 * (`GET /api/assets/:id/thumbnail?size=...`). Pas d'EXIF ni de hashes ici.
 */
export const galleryAssetSchema = z.object({
    id: z.uuid(),
    relPath: z.string(),
    filename: z.string(),
    kind: assetKindSchema,
    width: z.number().int().nonnegative().nullable(),
    height: z.number().int().nonnegative().nullable(),
    durationS: z.number().nonnegative().nullable(),
    takenAt: z.string().datetime({ offset: true }).nullable(),
    isMissing: z.boolean(),
    /** Note 0..5 (0 = non noté) — tri « les mieux notées ». */
    rating: z.number().int().min(0).max(5),
    /** Compteur de vues global (≥ 0) — tri « les plus vues ». */
    viewCount: z.number().int().nonnegative(),
    /**
     * Un humain a-t-il statué sur les tags de cette photo (nouveau système « Taguer »,
     * drapeau `assets.tagged`) ? La tuile peut ainsi montrer un état « taguée ».
     *
     * OPTIONNEL au CONTRAT (mais TOUJOURS peuplé par l'API) : rendu optionnel pour rester
     * NON cassant vis-à-vis des constructeurs d'objets `GalleryAsset` existants (mocks de
     * test typés en littéral, tuiles). Le back renvoie systématiquement `true`/`false` ;
     * un lot ultérieur pourra le rendre requis quand tous les producteurs le fourniront.
     */
    tagged: z.boolean().optional(),
    /**
     * VERSION courte des octets du fichier (préfixe 8 car. du `file_hash`) — cache-buster
     * de la VIGNETTE. Une écriture EXIF (dont la rotation par le tag Orientation, outil
     * « Pivoter ») change `file_hash` SANS changer l'`identity_hash` : la clé de la vignette
     * (`identity_hash + taille`), servie en `Cache-Control: immutable` (~1 an), resterait
     * inchangée → l'ancienne orientation s'afficherait longtemps. Le client appose ce `rev`
     * en `&v=` sur l'URL de vignette pour forcer une nouvelle URL après rotation.
     *
     * OPTIONNEL au CONTRAT (mais TOUJOURS peuplé par l'API galerie) : rendu optionnel pour
     * rester NON cassant vis-à-vis des constructeurs d'objets `GalleryAsset` existants (mocks
     * de test, tuiles). Absent ⇒ le client n'appose simplement pas de buster (cache normal).
     */
    rev: z.string().optional(),
});
/**
 * Page d'assets. `total` = nombre total d'assets correspondant au filtre (hors
 * fenêtre), pour dimensionner le scroller virtualisé. `nextCursor` : offset de la
 * page suivante (chaîne), absent en fin de liste (compat future keyset).
 */
export const assetPageSchema = z.object({
    items: z.array(galleryAssetSchema),
    total: z.number().int().nonnegative(),
    nextCursor: z.string().optional(),
});
/**
 * Facette d'un dossier (onglet Dossiers) : préfixe + nombre d'assets présents +
 * de quoi peindre une CARTE de dossier (grille de dossiers, Lot H) :
 *  - `coverAssetId` : asset REPRÉSENTATIF du dossier (« première image ») pour la
 *    vignette de couverture (`GET /api/assets/:id/thumbnail`). `null` si le dossier
 *    ne contient aucun asset présent exploitable → placeholder côté UI.
 *  - `latestTakenAt` : date de prise de vue la plus récente du dossier (ISO 8601),
 *    `null` si aucun asset daté → tri des cartes par date.
 */
export const folderFacetSchema = z.object({
    /** Segment/préfixe de dossier (dérivé de `rel_path`), '' pour la racine. */
    path: z.string(),
    count: z.number().int().nonnegative(),
    /** Id de l'asset de couverture (« première image »), `null` si aucun. */
    coverAssetId: z.string().uuid().nullable(),
    /** Date de prise de vue la plus récente du dossier (ISO 8601), `null` si aucune. */
    latestTakenAt: z.string().datetime({ offset: true }).nullable(),
});
export const folderFacetListSchema = z.object({
    folders: z.array(folderFacetSchema),
});
/**
 * Query params de `GET /api/libraries/:id/folders`. `parent` (optionnel) = chemin du
 * dossier COURANT dont on veut lister les SOUS-DOSSIERS DIRECTS (navigation
 * hiérarchique façon explorateur). Absent ⇒ RACINE (dossiers de 1er niveau =
 * comportement historique). Chaque `path` renvoyé est alors le chemin COMPLET du
 * sous-dossier (ex. `parent=2024` → `2024/vacances`). Borné en longueur ; jamais
 * concaténé dans le SQL (transite comme paramètre lié, `like_escape` sur le préfixe).
 */
export const folderFacetQuerySchema = z.object({
    parent: z.string().max(1024).optional(),
});
/** Facette d'un tag (onglet Tags) : id + label + nombre d'assets validés. */
export const tagFacetSchema = z.object({
    id: z.uuid(),
    label: z.string(),
    count: z.number().int().nonnegative(),
});
export const tagFacetListSchema = z.object({
    tags: z.array(tagFacetSchema),
});
/**
 * Compteurs de la barre d'outils (XPHOTO-20) : total présents + ventilation par
 * type + nombre de vidéos avec son + nombre « À valider (IA) ».
 */
export const assetCountsSchema = z.object({
    present: z.number().int().nonnegative(),
    /**
     * Nombre de photos « À valider (IA) » : non taguées (`tagged = false`) porteuses d'AU
     * MOINS UNE liaison de tag proposée par l'IA (`asset_tags.source = 'ai'`, quel que soit
     * son statut). Badge du raccourci d'en-tête « À valider (IA) » — aligné EXACTEMENT sur
     * le filtre `pendingAiTags` de la galerie (une photo dont on a « accepté » les
     * suggestions IA reste À VALIDER tant qu'un humain ne l'a pas finalisée `tagged = true`).
     */
    aiReview: z.number().int().nonnegative(),
    /**
     * Ventilation par type (mêmes règles de présence que `present`), pour la section
     * « Type » de la barre d'outils. `image` = images STATIQUES seulement (les animées
     * en sont EXCLUES), `animatedImage` = images animées (GIF/WebP/APNG), `video` =
     * vidéos. Somme des trois = `present`.
     */
    byKind: z.object({
        image: z.number().int().nonnegative(),
        animatedImage: z.number().int().nonnegative(),
        video: z.number().int().nonnegative(),
    }),
    /** Nombre de vidéos avec piste audio (`has_audio = true`) — facette « Avec son ». */
    withAudio: z.number().int().nonnegative(),
});
/**
 * Position (rang) d'un asset dans l'ensemble ORDONNÉ courant (mêmes tri/filtres que
 * la liste). `index` = rang 0-based dans la liste paginée équivalente (donc
 * `list(offset=index, limit=1)` renvoie exactement cet asset) ; `total` = nombre
 * total d'assets de l'ensemble filtré.
 *
 * Sert à la VISIONNEUSE : ouverte à froid (`/photo/:id`) ou hors fenêtre chargée,
 * elle obtient l'index absolu de l'asset pour naviguer préc./suiv. de façon FIABLE
 * sur TOUT l'ensemble (et non plus seulement sur le préfixe de grille chargé), dans
 * exactement le même ordre que la galerie. 404 si l'asset n'appartient pas à
 * l'ensemble (filtré / hors sous-arbre accordé).
 */
export const assetPositionSchema = z.object({
    index: z.number().int().nonnegative(),
    total: z.number().int().nonnegative(),
});
/**
 * Résultat de `GET /api/libraries/:id/assets/all-ids` : la liste EXHAUSTIVE des ids
 * des assets correspondant au FILTRE COURANT (mêmes filtres que la liste, sans
 * pagination). Sert au « Tout sélectionner » de la galerie : cocher l'ENSEMBLE du
 * groupe (dossier/tag/filtre/à-traiter) et non seulement les ~100 tuiles chargées,
 * pour agir en masse (note/tags/déplacement/suppression/tag IA) sur des milliers
 * d'assets. Ordre non pertinent (on remplit un `Set`).
 */
export const assetAllIdsResultSchema = z.object({
    ids: z.array(z.uuid()),
});
/** Tailles de vignette servies par l'API (alignées sur le scanner T1-C). */
export const thumbnailSizeSchema = z.enum(['256', '1024']);
/** Query de `GET /api/assets/:id/thumbnail` (taille par enum, défaut 256). */
export const thumbnailQuerySchema = z.object({
    size: thumbnailSizeSchema.default('256'),
    /**
     * Cache-buster OPTIONNEL et IGNORÉ par le serveur (le contenu est déterminé par
     * l'`identity_hash`, jamais par ce paramètre). Le client y appose le `rev` (préfixe du
     * `file_hash`) pour obtenir une NOUVELLE URL après une écriture EXIF (rotation) : la
     * vignette est servie `immutable` (~1 an), sans buster l'ancienne orientation resterait
     * affichée. Accepté ici pour ne PAS rejeter le paramètre (400) ; jamais lu par le handler.
     */
    v: z.string().optional(),
});
//# sourceMappingURL=asset-query.js.map